// Used by session-globals; DO NOT import the store, http, or any large resources in here
import get from 'lodash/get';
import { FREE_CONSTANTS, USER_ROLES } from '@/constants';
import { $can } from '@pendo/permissions';
import { APPS } from '@pendo/services/Constants';
import { SUBSCRIPTION_ADMIN_ROLE } from '@/settings/user-management/utils/roles-and-permissions.utils';

const { WEB_PLATFORM, EXTENSION_PLATFORM, MOBILE_PLATFORMS, ALL_PLATFORM } = APPS;

// Match AppsService aggregate ids without importing apps.service (it pulls session-globals, which imports this file).
const ALL_APPS_AGGREGATE_ID = 'all';
const MOBILE_PLATFORM = 'mobile';

export function getUserFromUserData (userData) {
    const { activeOrganization } = userData;

    // map userid -> id because we use id everywhere else
    const { userid, subid, ...user } = userData.user;
    const { role } = convertAdminFlagToRole(userData.user);

    return { ...user, role, id: userid, subscriptionId: subid, activeOrganization };
}

export function hasGlobalAccess (user) {
    // Check if exists and is non-zero; BE won't send if already expired
    return !!user.globalAccessExpiresAt;
}

export function isReadOnly (user) {
    return get(user, 'flags.readOnly', false);
}

export function isUser (user) {
    if (isReadOnly(user)) {
        return false;
    }

    return user.role === USER_ROLES.USER;
}

export function isAdmin (user) {
    return get(user, 'flags.admin', false);
}

export function isV2SubAdmin (user) {
    return !!user.subscriptionRoles?.includes(SUBSCRIPTION_ADMIN_ROLE);
}

export function isSuper (user) {
    return user.role === USER_ROLES.SUPER;
}

export function isSupport (user) {
    return get(user, 'userflags.supportFlag', false);
}

export function isGuest (user) {
    return !!user.expiresAt;
}

export function isOps (user) {
    return isSuper(user) && get(user, 'userflags.opsFlag', false);
}

export function isPendozer (user) {
    return get(user, 'isPendozer', false);
}

export function isAdoptManager (user) {
    return (isAdmin(user) && get(user, 'flags.adoptManager', false)) || isOps(user);
}

export function hasAdminAccess (user) {
    if (isAdmin(user)) {
        return true;
    }

    return user.role >= USER_ROLES.ADMIN;
}

export function roleName (user) {
    if (isGuest(user)) {
        return 'Guest';
    }

    if (isOps(user)) {
        return 'Ops';
    }

    if (isSuper(user)) {
        return 'Super';
    }

    if (isAdmin(user)) {
        return 'Admin';
    }

    if (isUser(user)) {
        return 'User';
    }

    return 'Read-Only';
}

// Mutates a user object to account for the change in the way that we
// promote `USER` to `ADMIN` post APP-2428, setting user.flags.admin
// whether user.role is USER_ROLES.ADMIN
export function convertAdminRoleToFlag (user) {
    // Does not apply to SUPERs...
    if (isSuper(user)) {
        return user;
    }

    // Set `admin` flag if `role === ADMIN`
    if (!user.flags) {
        user.flags = {};
    }
    user.flags.admin = user.role === USER_ROLES.ADMIN;

    // The `POST .../user` call will error if `role === User.Roles.ADMIN`
    if (get(user, 'flags.admin', false)) {
        user.role = USER_ROLES.USER;
    }

    return user;
}

//  Mutates a user object to account for the change in the way that we
//  promote `USER` to `ADMIN` pre APP-2428, making user.role match the
//  constants in USER_ROLES.
export function convertAdminFlagToRole (user) {
    // Does not apply to SUPERs...
    if (isSuper(user)) {
        return user;
    }

    if (get(user, 'flags.admin', false)) {
        user.role = USER_ROLES.ADMIN;
    }

    return user;
}

export const GUIDE_EDIT_RESTRICTION_FIELDS = [
    'group',
    'order',
    'schedule',
    'segment',
    'status',
    'conversion',
    'experiment',
];

export const RC_EDIT_RESTRICTION_FIELDS = ['segment', 'status'];

export const PERMITTED_APP_PLATFORMS = ['ios', 'android'];

export function alterApplicationAccessControl (accessControl, getters) {
    let altered = accessControl;

    // With this feature flag on we must edit some guide and rc permissions for non-mobile applications:
    //     1) no guide creates are allowed and will be removed
    //     2) all guide edits must have a specific set of fields that can be edited
    //     3) only RC edits and publishes are allowed
    //     4) for rc you can only edit segment and status
    if (getters['subscription/getFeatureFlag']('enableGuideCopyEditRestrictions')) {
        altered = Object.entries(accessControl).reduce((newAccessControl, [appId, perms]) => {
            const app = getters['apps/getAppById'](appId);

            if (!app) {
                return newAccessControl;
            }

            if (!PERMITTED_APP_PLATFORMS.includes(app.platform)) {
                const alteredPerms =
                    perms &&
                    perms.reduce((filteredPerms, perm) => {
                        switch (perm.entity) {
                            case 'guide':
                                if (perm.action === 'edit') {
                                    perm.fields = GUIDE_EDIT_RESTRICTION_FIELDS;
                                }

                                if (perm.action !== 'create') {
                                    filteredPerms.push(perm);
                                }
                                break;
                            case 'resourceCenter':
                                if (perm.action === 'edit') {
                                    perm.fields = RC_EDIT_RESTRICTION_FIELDS;
                                    filteredPerms.push(perm);
                                } else if (perm.action === 'publish') {
                                    filteredPerms.push(perm);
                                }
                                break;
                            default:
                                filteredPerms.push(perm);
                        }

                        return filteredPerms;
                    }, []);

                newAccessControl[appId] = alteredPerms;
            } else {
                newAccessControl[appId] = perms;
            }

            return newAccessControl;
        }, {});
    }

    return altered;
}

export function turnOffSampleData () {
    window.localStorage.removeItem(FREE_CONSTANTS.SampleDataTooltipDismissedKey);
    window.localStorage.removeItem(FREE_CONSTANTS.SampleDataLocalStorageKey);
}

export function isSampleDataTurnedOn () {
    return window.localStorage.getItem(FREE_CONSTANTS.SampleDataLocalStorageKey);
}

export function canViewSessionReplayForApp (appId) {
    return $can(appId)
        .entity('sessionReplay')
        .action('view');
}

export function filterSelectedAppsByAccess (selectedApps, accessibleAppIds, appsMap = {}) {
    const isAllApps = selectedApps?.length === 1
        && [ALL_PLATFORM, ALL_APPS_AGGREGATE_ID].includes(selectedApps[0].id);
    if (isAllApps) {
        return accessibleAppIds.map((id) => {
            const app = appsMap[id];

            return { id, platform: app?.platform };
        });
    }

    const isPlatformFilter = selectedApps?.length === 1
        && [WEB_PLATFORM, EXTENSION_PLATFORM, MOBILE_PLATFORM].includes(selectedApps[0].id);
    if (isPlatformFilter) {
        const platformId = selectedApps[0].id;
        const platformMatches = platformId === MOBILE_PLATFORM ? MOBILE_PLATFORMS : [platformId];

        return accessibleAppIds
            .filter((id) => {
                const app = appsMap[id];

                return app && platformMatches.includes(app.platform);
            })
            .map((id) => ({ id, platform: appsMap[id].platform }));
    }

    return selectedApps?.filter(({ id }) => accessibleAppIds.includes(id)) ?? [];
}
